import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";

export default function Auth() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  // Clear any existing "Keep Logged In" states when visiting Auth page
  // This guarantees they will see the prompt again after logging in
  useEffect(() => {
    localStorage.removeItem('keep_logged_in');
    localStorage.removeItem('keep_logged_in_decided');
    sessionStorage.removeItem('keep_logged_in');
    sessionStorage.removeItem('keep_logged_in_decided');
  }, []);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");

  const checkPasswordStrength = (pwd: string) => {
    const hasMinLength = pwd.length >= 8;
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    return hasMinLength && hasUpper && hasLower && hasNumber;
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!isLogin) {
      if (password !== confirmPassword) {
        setFormError("Passwords do not match.");
        return;
      }
      if (!checkPasswordStrength(password)) {
        setFormError("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
        return;
      }
    }

    setIsLoading(true);
    
    try {
      if (isLogin) {
        console.log("Attempting email login...");
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        
        if (!data.session) {
          toast({
            title: "Check your email",
            description: "You must confirm your email before you can sign in.",
            variant: "destructive"
          });
          return;
        }
        
        console.log("Login successful! Redirecting forcefully...");
        try {
          const { data: userRow, error: dbError } = await supabase.from('users').select('onboarding_completed').eq('id', data.user.id).maybeSingle();
          if (dbError) {
            console.error("Error fetching user profile:", dbError);
            window.location.href = "/";
            return;
          }
          if (!userRow || !userRow.onboarding_completed) {
            window.location.href = "/onboarding";
          } else {
            window.location.href = "/";
          }
        } catch (e) {
          window.location.href = "/";
        }
      } else {
        console.log("Attempting email signup...");
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/onboarding`,
            data: {
              first_name: firstName,
              last_name: lastName,
              full_name: `${firstName} ${lastName}`.trim()
            }
          },
        });
        console.log("Signup result:", { data, error });
        if (error) throw error;
        toast({
          title: "Account created",
          description: "Check your email for a confirmation link, or you might be logged in already.",
        });
        setTimeout(() => { window.location.href = "/"; }, 1500);
      }
    } catch (error: any) {
      console.error("Auth Exception:", error);
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });
      if (error) throw error;
    } catch (error: any) {
      setFormError(error.message);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {isLogin ? "Welcome back" : "Create an account"}
          </CardTitle>
          <CardDescription>
            {isLogin 
              ? "Enter your details to sign in to your account" 
              : "Enter your details to get started with Addvance"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            className="w-full flex items-center gap-2 mb-6" 
            onClick={handleGoogleLogin}
            type="button"
          >
            <svg className="h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
              <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
            </svg>
            {isLogin ? "Sign in with Google" : "Sign up with Google"}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          {formError && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md mb-6">
              {formError}
            </div>
          )}

          <form onSubmit={handleEmailAuth} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required={!isLogin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Surname</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required={!isLogin}
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email" 
                placeholder="m@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required 
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <Button
                    variant="ghost"
                    type="button"
                    className="p-0 h-auto text-xs text-primary hover:underline bg-transparent hover:bg-transparent"
                    onClick={async () => {
                      if (!email) {
                        setFormError("Please enter your email address to reset password.");
                        return;
                      }
                      setIsLoading(true);
                      setFormError("");
                      try {
                        const { error } = await supabase.auth.resetPasswordForEmail(email, {
                          redirectTo: `${window.location.origin}/`,
                        });
                        if (error) throw error;
                        toast({
                          title: "Password Reset",
                          description: "Check your email for a password reset link.",
                        });
                      } catch (error: any) {
                        setFormError(error.message);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                  >
                    Forgot password?
                  </Button>
                )}
              </div>
              <Input 
                id="password"
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required 
              />
              {!isLogin && password.length > 0 && (
                <div className="text-xs space-y-1 mt-2">
                  <p className={password.length >= 8 ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                    {password.length >= 8 ? "✓" : "○"} At least 8 characters
                  </p>
                  <p className={/[A-Z]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                    {/[A-Z]/.test(password) ? "✓" : "○"} At least one uppercase letter
                  </p>
                  <p className={/[a-z]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                    {/[a-z]/.test(password) ? "✓" : "○"} At least one lowercase letter
                  </p>
                  <p className={/[0-9]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                    {/[0-9]/.test(password) ? "✓" : "○"} At least one number
                  </p>
                </div>
              )}
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input 
                  id="confirmPassword"
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required 
                />
              </div>
            )}

            <Button className="w-full" type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLogin ? "Sign In" : "Sign Up"}
            </Button>
          </form>
        </CardContent>
        <CardFooter>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setIsLogin(!isLogin);
              setFormError("");
            }}
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
